import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { enAU } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { IntentButton } from "./intent-button";
import { formatDate } from "@/lib/format";

export interface AllmarkDatePickerProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function AllmarkDatePicker({
  value,
  onChange,
  disabled,
  placeholder = "dd/MM/yyyy",
}: AllmarkDatePickerProps) {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IntentButton
          intent="ghost"
          disabled={disabled}
          aria-label="Pick a date"
          className="w-[180px] justify-start font-normal"
        >
          <CalendarIcon className="size-4" />
          {value ? formatDate(value) : <span className="text-muted-foreground">{placeholder}</span>}
        </IntentButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          locale={enAU}
          selected={date}
          onSelect={(d) => {
            // d is local midnight on the picked day; toISOString() returns UTC,
            // so the raw value may look like the previous day in non-AU TZ logs.
            // formatDate() recovers the correct day via formatInTimeZone(Sydney).
            onChange(d ? d.toISOString() : null);
            setOpen(false);
          }}
          weekStartsOn={1}
          formatters={{ formatCaption: (d) => format(d, "MMMM yyyy", { locale: enAU }) }}
        />
      </PopoverContent>
    </Popover>
  );
}
